import React from 'react';
import { fx } from '../../fx.js';
import { BODY, ink } from '../../primitives.js';

const h = React.createElement;

/**
 * Segmented control with one sliding thumb (gold); `variant="tabs"` is the tab strip (lime-tint
 * thumb, no frame). Replaces v1 Tabs.
 *
 * @param {object} p
 * @param {string|number} p.value
 * @param {(v: string|number) => void} [p.onChange]
 * @param {{ value: string|number; label: React.ReactNode }[]} p.options
 * @param {'control'|'tabs'} [p.variant]
 * @param {'sm'|'md'} [p.size]
 * @param {boolean} [p.shadow]
 * @param {number|string} [p.width]
 * @param {number} [p.padX]
 * @param {React.CSSProperties} [p.style]
 */
export function Seg({ value, onChange, options, variant = 'control', size = 'md', shadow = true, width, padX = 14, style }) {
  const wrap = React.useRef(null);
  const [thumb, setThumb] = React.useState(null);
  const thumbRef = React.useRef(null), first = React.useRef(true);
  React.useEffect(() => { if (first.current) { first.current = false; return; } const f = fx; if (f.reduced && !f.reduced()) f.animate(thumbRef.current, [{ scale: '1 1' }, { scale: '1.12 .78' }, { scale: '.96 1.06' }, { scale: '1 1' }], { duration: 340, easing: 'ease-out' }); }, [value]);
  React.useLayoutEffect(() => {
    const w = wrap.current; if (!w) return;
    const el = w.querySelector(`[data-v="${String(value)}"]`);
    if (el) setThumb({ left: el.offsetLeft, width: el.offsetWidth });
  }, [value, options.length]);
  const tabs = variant === 'tabs', sm = size === 'sm';
  return h('div', {
    ref: wrap, role: 'tablist',
    style: {
      position: 'relative', display: 'inline-flex', width, padding: 3, boxSizing: 'border-box',
      background: tabs ? 'transparent' : 'var(--card)', border: tabs ? 'none' : ink(2),
      borderRadius: tabs ? 0 : '10px 10px 10px var(--r-cut)', boxShadow: tabs || !shadow ? 'none' : 'var(--el-1)', ...style
    }
  },
    thumb && h('div', {
      ref: thumbRef, 'aria-hidden': true,
      style: {
        position: 'absolute', top: 3, bottom: 3, left: thumb.left, width: thumb.width,
        background: tabs ? 'var(--sel)' : 'var(--gold)', border: tabs ? ink(1.5) : ink(1.5), borderRadius: 7,
        boxShadow: tabs ? 'none' : '1px 1px 0 var(--ink)', boxSizing: 'border-box',
        transition: 'left var(--dur-base) var(--ease-pop), width var(--dur-base) var(--ease-pop)'
      }
    }),
    options.map((o) => h('button', {
      key: String(o.value), type: 'button', role: 'tab', 'data-v': String(o.value), 'aria-selected': o.value === value,
      onClick: () => onChange && onChange(o.value),
      style: {
        position: 'relative', zIndex: 1, background: 'transparent', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
        padding: sm ? `4px ${padX - 3}px` : `6px ${padX}px`, fontFamily: BODY, fontSize: sm ? 11.5 : 12.5,
        fontWeight: o.value === value ? 800 : sm ? 600 : 700, color: o.value === value ? (tabs ? 'var(--tx)' : 'var(--on-accent)') : sm ? 'var(--mut)' : 'var(--tx)'
      }
    }, o.label))
  );
}
